import { useState } from 'react';
import { useI18n } from '../../i18n';
import { TrophyIcon } from '../shared/Icons';
import LogoEditOverlay from '../shared/LogoEditOverlay';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import CountryPicker from '../shared/CountryPicker';
import Field from '../shared/Field';
import { inlineError } from '../../hooks/useApiCall';
import { useBusy } from '../../hooks/useBusy';
import type { Tournament, CreateTournamentDto } from '../../types';
import { V3_AGE_GROUP_CODES } from '../../utils/tournament';
import { toLocalDay } from '../../utils/calendarDate';
import { TOURNAMENT_TYPES, DEFAULT_TOURNAMENT_TYPE, type TournamentType } from '../../utils/tournamentType';
import { useLabels } from '../../i18n/labels';
import Notice from '../shared/Notice';

/** #532 — the longest description the API takes (migration 003 CHECK). */
const DESCRIPTION_MAX = 2000;

const EMPTY_FORM = {
  name: '', location: '', country: 'LT', date_start: '', date_end: '',
  type: DEFAULT_TOURNAMENT_TYPE as TournamentType,
  facebook_url: '', livestream_url: '', contact_phone: '', registration_deadline: '',
  description: '',
};

/** Partial — caller receives whatever fields the user filled in. For create
 *  it's a full `CreateTournamentDto`; for edit it's an `UpdateTournamentDto`
 *  slice. Caller narrows at submit time (see TournamentList.tsx handleCreate). */
export type TournamentFormData = Partial<CreateTournamentDto>;

interface Props {
  /** Pass existing tournament for edit mode, or undefined for create */
  tournament?: Tournament;
  onSave: (data: TournamentFormData) => Promise<void>;
  onClose: () => void;
  /** Edit-only: logo actions */
  onUploadLogo?: () => void;
  onRemoveLogo?: () => void;
  /** #605 — the removal of the logo runs. The trash button is disabled until the answer. */
  removeLogoDisabled?: boolean;
  /** Edit-only: delete tournament (renders top-right TrashIcon in header) */
  onDelete?: () => void;
}

/** A UTC instant from the API -> the `YYYY-MM-DDTHH:mm` a `datetime-local`
 *  control expects, in the BROWSER's timezone. */
function utcToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${toLocalDay(d)}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The local wall-clock value of a `datetime-local` control -> a UTC instant.
 *  `new Date('2026-09-26T22:00')` is parsed in the browser's timezone, which is
 *  exactly what the operator typed, so toISOString() gives the right moment. */
function localInputToUtc(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function TournamentFormModal({ tournament, onSave, onClose, onUploadLogo, onRemoveLogo, removeLogoDisabled, onDelete }: Props) {
  const { t } = useI18n();
  const { typeLabel } = useLabels();
  const isEdit = !!tournament;

  const [form, setForm] = useState(isEdit ? {
    name:                  tournament.name || '',
    location:              tournament.location || '',
    country:               tournament.country || 'LT',
    date_start:            tournament.date_start ? String(tournament.date_start).slice(0, 10) : '',
    date_end:              tournament.date_end   ? String(tournament.date_end).slice(0, 10)   : '',
    type:                  (tournament.type || DEFAULT_TOURNAMENT_TYPE) as TournamentType,
    facebook_url:          tournament.facebook_url || '',
    livestream_url:        tournament.livestream_url || '',
    contact_phone:         tournament.contact_phone || '',
    // `datetime-local` means LOCAL wall-clock. The API sends a UTC instant, so
    // convert before handing it over. Slicing the ISO string put the UTC hour
    // into a control that means local — which cancelled out the write bug
    // below and made the form round-trip look correct while the stored
    // instant was 2-3 hours off. See #322.
    registration_deadline: tournament.registration_deadline
      ? utcToLocalInput(tournament.registration_deadline) : '',
    description:           tournament.description || '',
  } : { ...EMPTY_FORM });

  // #532 — the age groups the tournament takes (operator, 2026-09-29). The
  // "All age groups" switch is NULL. When the manager turns it off, every
  // box starts ticked and the manager unticks the groups the tournament does
  // not take. Every box ticked is NULL too: "all" has one stored form.
  const [allAgeGroups, setAllAgeGroups] = useState(!tournament?.allowed_age_groups);
  const [ageGroups, setAgeGroups] = useState<string[]>(tournament?.allowed_age_groups ?? [...V3_AGE_GROUP_CODES]);
  const noAgeGroup = !allAgeGroups && ageGroups.length === 0;
  function toggleAgeGroup(code: string) {
    setAgeGroups(list => list.includes(code) ? list.filter(c => c !== code) : [...list, code]);
  }
  function toggleAllAgeGroups(on: boolean) {
    setAllAgeGroups(on);
    if (!on) setAgeGroups([...V3_AGE_GROUP_CODES]);
  }

  // #605, #664 — the lock of Save. The write is in the parent, so the lock is here.
  const saving = useBusy();
  const [err, setErr] = useState('');

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (noAgeGroup) return;
    await saving.run(async () => {
      setErr('');
      try {
        await onSave({
          name:                  form.name,
          location:              form.location,
          country:               form.country,
          date_start:            form.date_start,
          // A cleared field is `null`, never `undefined` (#452): JSON drops an
          // `undefined` key, the PATCH never saw it, and the old value stayed
          // while the modal closed as saved. An empty end date means a one-day
          // tournament, the default that create applies.
          date_end:              form.date_end || form.date_start,
          type:                  form.type as TournamentType,
          facebook_url:          form.facebook_url || null,
          livestream_url:        form.livestream_url || null,
          contact_phone:         form.contact_phone || null,
          // Send a real instant. The raw control value carries no offset, so
          // Postgres (session TimeZone=UTC) read a Lithuanian 22:00 as 22:00 UTC
          // and the deadline landed at 01:00 the next day. #322
          registration_deadline: localInputToUtc(form.registration_deadline),
          // #532 — a cleared description is null (#452). The list goes in
          // AG_ORDER order; every age group, ticked or by the switch, is null.
          description:           form.description.trim() || null,
          allowed_age_groups:    allAgeGroups || ageGroups.length === V3_AGE_GROUP_CODES.length
            ? null : V3_AGE_GROUP_CODES.filter(c => ageGroups.includes(c)),
        });
      } catch (e: unknown) {
        // The translated message, with its params: the raw English `error`
        // could not name the days of TOURNAMENT_DAYS_IN_USE in Lithuanian (#455).
        setErr(inlineError(e));
      }
    });
  }

  // #552 — the panel layout: Save stays at the bottom of the dialog. On a
  // phone it was at the end of 1030 px (Create) or 1260 px (Edit) of fields.
  return (
    <ModalBackdrop onClose={onClose} maxWidth="lg" layout="panel" ariaLabel={isEdit ? t.tournamentForm.ariaEdit : t.tournamentForm.ariaCreate}>
      <ModalHeader
        title={isEdit ? t.tournamentForm.editTitle : t.tournament.create}
        subtitle={isEdit ? tournament.name : undefined}
      />

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <ModalBody className="space-y-4">
        {/* Logo (edit only) */}
        {isEdit && (
          <div className="relative group w-full rounded-xl overflow-hidden">
            {tournament.logo_url
              ? <img src={tournament.logo_url} alt="" className="w-full h-32 object-cover" />
              : <div className="w-full h-32 bg-gray-100 flex items-center justify-center text-gray-400"><TrophyIcon className="w-12 h-12" /></div>
            }
            {onUploadLogo && (
              // #552 — visible on a touch screen: an invisible button took
              // the tap, closed the modal and lost the edits (#661: one shared overlay).
              <LogoEditOverlay size="lg" onChange={onUploadLogo}
                onRemove={onRemoveLogo && tournament.logo_url ? onRemoveLogo : undefined} removeDisabled={removeLogoDisabled}
                changeLabel={t.tournament.changeLogo} removeLabel={t.tournament.removeLogo} />
            )}
          </div>
        )}

        {/* Form fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t.tournament.tournamentName} required className="sm:col-span-2">
            <input className="input" value={form.name} onChange={set('name')} data-testid="tournament-form-name" />
          </Field>
          <Field label={t.common.location} required className="sm:col-span-2">
            <input className="input" value={form.location} onChange={set('location')} data-testid="tournament-form-location" />
          </Field>
          <Field label={t.country.label} className="sm:col-span-2">
            <CountryPicker value={form.country} onChange={code => setForm(f => ({ ...f, country: code }))} />
          </Field>
          <Field label={t.common.date} required>
            <input type="date" className="input" min="2020-01-01" max="2099-12-31"
              value={form.date_start} onChange={set('date_start')} data-testid="tournament-form-date-start" />
          </Field>
          <Field label={t.tournamentForm.endDate} hint={t.tournamentForm.multiDay}>
            {/* #455 — the end cannot come before the start (the server refuses it). */}
            <input type="date" className="input" min={form.date_start || '2020-01-01'} max="2099-12-31"
              value={form.date_end} onChange={set('date_end')} data-testid="tournament-form-date-end" />
          </Field>
          <Field label={t.tournamentForm.type} htmlFor="tournament-form-type" className="sm:col-span-2">
            {/* #536 — the options come from the one list of the types. */}
            <select id="tournament-form-type" className="input" value={form.type} onChange={set('type')} data-testid="tournament-type">
              {TOURNAMENT_TYPES.map(type => <option key={type} value={type}>{typeLabel(type)}</option>)}
            </select>
            {isEdit && <p className="text-xs text-gray-400 mt-1">{t.tournament.typeLockedHint}</p>}
          </Field>
        </div>
        <Field label={t.tournamentForm.description} optional htmlFor="tournament-form-description">
          <textarea id="tournament-form-description" className="input min-h-[5rem]" rows={4} maxLength={DESCRIPTION_MAX} data-testid="tournament-description"
            value={form.description} onChange={set('description')} />
          <p className="text-[10px] text-gray-400 text-right">{form.description.length}/{DESCRIPTION_MAX}</p>
        </Field>
        <div data-testid="tournament-age-groups">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" className="w-4 h-4" checked={allAgeGroups} data-testid="all-age-groups"
              onChange={e => toggleAllAgeGroups(e.target.checked)} />
            {t.tournamentForm.allAgeGroups}
          </label>
          {!allAgeGroups && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
              {V3_AGE_GROUP_CODES.map(code => (
                <label key={code} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" className="w-3.5 h-3.5" checked={ageGroups.includes(code)}
                    data-testid={`age-group-${code}`} onChange={() => toggleAgeGroup(code)} />
                  {code}
                </label>
              ))}
            </div>
          )}
          {noAgeGroup && <p className="text-xs text-red-600 mt-1">{t.tournamentForm.ageGroupsNone}</p>}
        </div>
        <Field label={t.tournament.facebookEvent}>
          <input type="url" className="input" placeholder="https://facebook.com/events/..."
            value={form.facebook_url} onChange={set('facebook_url')} />
        </Field>
        <Field label={t.tournament.livestream}>
          <input type="url" className="input" placeholder="https://youtube.com/watch?v=..."
            value={form.livestream_url} onChange={set('livestream_url')} />
        </Field>
        <Field label={t.tournamentForm.contactPhone} optional>
          <input type="tel" className="input" placeholder="+370 600 00000"
            value={form.contact_phone} onChange={set('contact_phone')} />
        </Field>
        <Field label={t.tournament.regDeadline} optional>
          <input type="datetime-local" className="input" value={form.registration_deadline}
            onChange={set('registration_deadline')} />
        </Field>

        {err && <Notice tone="error" size="xs">{err}</Notice>}
      </ModalBody>

        <ModalFooter
          actions={onDelete ? [{ label: t.tournament.delete, onClick: onDelete, destructive: true, testId: 'tournament-delete' }] : undefined}
          saveAsSubmit
          busy={saving.busy}
          saveLabel={isEdit ? t.common.save : t.common.create}
          saveDisabled={noAgeGroup}
          onCancel={onClose}
        />
      </form>
    </ModalBackdrop>
  );
}
